import { useEffect, useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { GlassButton } from '@/components/ui/GlassButton'
import { Zap } from 'lucide-react'
import { useT } from '@/i18n/provider'

export function HeroSection() {
  const t = useT()
  const sectionRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const pointerTargetRef = useRef({ x: 0, y: 0 })
  const pointerCurrentRef = useRef({ x: 0, y: 0 })
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const section = sectionRef.current
    const content = contentRef.current
    if (!section || !content) return

    const onPointerMove = (event: PointerEvent) => {
      const rect = section.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2
      pointerTargetRef.current = {
        x: Math.max(-1, Math.min(1, x)),
        y: Math.max(-1, Math.min(1, y)),
      }
    }

    const onPointerLeave = () => {
      pointerTargetRef.current = { x: 0, y: 0 }
    }

    const animate = () => {
      const current = pointerCurrentRef.current
      const target = pointerTargetRef.current
      current.x += (target.x - current.x) * 0.08
      current.y += (target.y - current.y) * 0.08

      section.style.setProperty('--hero-mx', current.x.toFixed(4))
      section.style.setProperty('--hero-my', current.y.toFixed(4))
      content.style.transform = `translate3d(${(current.x * 14).toFixed(2)}px, ${(current.y * 10).toFixed(2)}px, 0)`

      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)
    section.addEventListener('pointermove', onPointerMove)
    section.addEventListener('pointerleave', onPointerLeave)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }
      section.removeEventListener('pointermove', onPointerMove)
      section.removeEventListener('pointerleave', onPointerLeave)
    }
  }, [])

  return (
    <section ref={sectionRef} className="hero-stage relative min-h-screen flex items-center justify-center px-6 pt-20">
      <div className="hero-vignette absolute inset-0 pointer-events-none" aria-hidden="true" />
      <div className="hero-aurora absolute inset-0 pointer-events-none" aria-hidden="true" />
      <div className="hero-noise absolute inset-0 pointer-events-none" aria-hidden="true" />
      <div className="hero-orb hero-orb-a pointer-events-none" aria-hidden="true" />
      <div className="hero-orb hero-orb-b pointer-events-none" aria-hidden="true" />

      {/* Content */}
      <div ref={contentRef} className="hero-content relative z-10 max-w-4xl mx-auto text-center">
        <div className="space-y-8 animate-fade-up">
          {/* Eyebrow badge */}
          <div className="hero-badge inline-flex items-center gap-2 glass-sm px-4 py-1.5">
            <span className="w-2 h-2 rounded-full bg-[var(--oc-accent-light)] animate-pulse" />
            <span className="text-xs font-medium text-white/65">{t('landing.hero.badge')}</span>
            <Zap size={12} className="text-[var(--oc-accent-light)]" />
          </div>

          {/* Headline */}
          <h1 className="hero-title text-5xl md:text-7xl font-black tracking-tight leading-[1.05] text-white">
            {t('landing.hero.title')}{' '}
            <span className="gradient-text glow-text">
              {t('landing.hero.titleAccent')}
            </span>
          </h1>

          {/* Subheadline */}
          <p className="hero-subtitle text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed">
            {t('landing.hero.subtitle')}
          </p>

          {/* CTAs */}
          <div className="hero-actions flex items-center justify-center gap-4 flex-wrap">
            <Link to="/auth/register">
              <GlassButton size="lg" variant="primary" className="hero-cta hero-cta-primary">
                {t('landing.hero.ctaPrimary')}
              </GlassButton>
            </Link>
            <Link to="/auth/login">
              <GlassButton size="lg" variant="secondary" className="hero-cta">
                {t('landing.hero.ctaSecondary')}
              </GlassButton>
            </Link>
          </div>

          {/* Provider logos */}
          <p className="hero-provider text-xs text-white/25 pt-4">
            {t('landing.hero.providerSupport')}
          </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="hero-scroll absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/20 animate-fade-in">
        <div className="hero-scroll-line w-px h-8 bg-gradient-to-b from-transparent to-white/20" />
        <span className="text-xs">{t('landing.hero.scroll')}</span>
      </div>
    </section>
  )
}
